import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { playClip } from '@src/ecs/archetypes';
import { Anim, ClipId, DragonTag, Dying, Enemy, EnemyKind, FxTag, Health, Player, Position, Radius, Sprite, SpriteId, Velocity } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, ParticleKind, type GameContext } from '@src/ecs/world';
import { Anims } from '@src/game/AnimDefs';

const RUN_THRESHOLD = 0.6;
/** Squared speed below which a moving character snaps back to Idle. */
const RUN_THRESHOLD_SQ = RUN_THRESHOLD * RUN_THRESHOLD;
/**
 * Facing hysteresis for the flip-only wizard art: a direction only flips the body once it
 * points at least this far past straight up/down, so diagonals and vertical runs don't flicker.
 */
const FACE_HYSTERESIS_DEG = 18;
const FACE_FLIP_SIN = Math.sin((FACE_HYSTERESIS_DEG * Math.PI) / 180);

/**
 * Picks clips from gameplay state, advances frame timers, decays the procedural
 * deformation channels, and emits footstep dust. Runs in the tick so death
 * clip timing is deterministic; the renderer only reads Anim.frame.
 */
export class AnimationSystem implements GameSystem {
  private ctx!: GameContext;

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    const a = Config.anim;
    const squashK = Math.exp(-a.squashDecay * dt);
    const popK = Math.exp(-a.popDecay * dt);
    const flashK = Math.exp(-a.flashDecay * dt);
    const joltK = Math.exp(-a.joltDecay * dt);

    const drawables = query(ctx.world, [Sprite, Anim]);
    for (let i = 0; i < drawables.length; i++) {
      const eid = drawables[i];
      Sprite.squash[eid] *= squashK;
      Sprite.pop[eid] *= popK;
      Sprite.flash[eid] *= flashK;
      Sprite.joltX[eid] *= joltK;
      Sprite.joltY[eid] *= joltK;
      this.selectClip(eid);
      this.advance(eid, dt);
    }
  }

  private selectClip(eid: number): void {
    const id = Sprite.id[eid] as SpriteId;
    // Death is set by startDeath and always wins; FX just play Idle once.
    if (Dying.flag[eid] || FxTag.flag[eid]) return;
    if (Anim.lock[eid]) {
      if (Anim.done[eid]) Anim.lock[eid] = 0;
      else return;
    }

    switch (id) {
      case SpriteId.Player: {
        if (Player.dead[eid]) return;
        const vx = Velocity.x[eid];
        const vy = Velocity.y[eid];
        const sp2 = vx * vx + vy * vy;
        const prevFace = Sprite.face[eid];
        if (
          Health.invuln[eid] > 0 &&
          Health.invuln[eid] <= Config.player.invulnAfterHit &&
          Health.invuln[eid] > Config.player.invulnAfterHit - Config.anim.hurtSeconds
        ) {
          playClip(eid, ClipId.Hurt, false, true);
          Anim.speed[eid] = 1;
        } else if (Player.firing[eid]) {
          // Cast wins over Run so shooting while strafing still reads as casting.
          playClip(eid, ClipId.Cast);
          Anim.speed[eid] = Player.recoil[eid] > 0.2 ? 1.45 : 1.15;
        } else if (sp2 > RUN_THRESHOLD_SQ) {
          playClip(eid, ClipId.Run);
          Anim.speed[eid] = Math.max(0.5, Math.sqrt(sp2) / Config.anim.runRefSpeed.player);
        } else {
          playClip(eid, ClipId.Idle);
          Anim.speed[eid] = 1;
        }
        // Body faces the aim only while actively aiming; otherwise it follows the intended
        // move direction (input, not Velocity, so knockback and slide never turn it) and
        // holds the last facing when idle.
        if (Player.aiming[eid]) {
          faceToward(eid, Player.aimX[eid], Player.aimY[eid]);
        } else {
          faceToward(eid, Player.moveX[eid], Player.moveY[eid]);
        }
        // Snappy facing flip: brief wide squash as the silhouette mirrors.
        if (prevFace !== 0 && Sprite.face[eid] !== prevFace) {
          Sprite.squash[eid] = Math.max(Sprite.squash[eid], Config.anim.player.faceSnapSquash);
          Sprite.pop[eid] = Math.max(Sprite.pop[eid], 0.18);
        }
        break;
      }
      case SpriteId.Gargoyle:
      case SpriteId.Spitter: {
        const kind = Enemy.kind[eid] as EnemyKind;
        if ((kind === EnemyKind.Gargoyle || kind === EnemyKind.Spitter) && Enemy.stagger[eid] > Config.anim.hurtSeconds * 0.4) {
          playClip(eid, ClipId.Hurt, Anim.done[eid] === 1, true);
        } else if (Enemy.mode[eid] === 1) {
          playClip(eid, ClipId.Cast);
        } else {
          playClip(eid, ClipId.Idle);
        }
        Anim.speed[eid] = 1;
        break;
      }
      case SpriteId.Imp:
      case SpriteId.Golem:
      case SpriteId.Skull:
      case SpriteId.Slime:
      case SpriteId.Bat:
      case SpriteId.Wraith:
      case SpriteId.Hexer:
      case SpriteId.Cinder:
      case SpriteId.Blight:
      case SpriteId.Brute:
      case SpriteId.Mimic: {
        const vx = Velocity.x[eid];
        const vy = Velocity.y[eid];
        const sp2 = vx * vx + vy * vy;
        const kind = Enemy.kind[eid] as EnemyKind;
        const golem = kind === EnemyKind.Golem;
        const brute = kind === EnemyKind.Brute;
        const mode = Enemy.mode[eid];
        // A fresh stagger on a big enemy plays the flinch clip and holds it; small fry just get shoved.
        if ((golem || brute) && Enemy.stagger[eid] > Config.anim.golemStagger - Config.anim.hurtSeconds) {
          // Restart only once the previous flinch finished so sustained fire keeps it reeling.
          playClip(eid, ClipId.Hurt, Anim.done[eid] === 1, true);
          Anim.speed[eid] = 1;
        } else if (golem && mode >= 1) {
          // Wind-up holds still; the stomp is the lunge clip.
          playClip(eid, mode === 1 ? ClipId.Idle : ClipId.Run);
          Anim.speed[eid] = mode === 1 ? 0.7 : 1.6;
        } else if (brute && mode >= 1) {
          // Wind: stock still. Charge: the walk clip at a sprint. Dazed: the flinch, held.
          if (mode === 3) {
            playClip(eid, ClipId.Hurt, false, true);
            Anim.speed[eid] = 0.6;
          } else {
            playClip(eid, mode === 1 ? ClipId.Idle : ClipId.Run);
            Anim.speed[eid] = mode === 1 ? 0.6 : 2.4;
          }
        } else if (kind === EnemyKind.Imp && mode >= 2) {
          playClip(eid, mode === 2 ? ClipId.Idle : ClipId.Run);
          Anim.speed[eid] = mode === 2 ? 0.75 : 1.55;
        } else if (kind === EnemyKind.Hexer) {
          // Wind-up is the cast clip (imp sheet borrows idle); kiting is a run.
          if (mode === 1) {
            playClip(eid, ClipId.Cast);
            Anim.speed[eid] = 1.2;
          } else if (sp2 > RUN_THRESHOLD_SQ) {
            playClip(eid, ClipId.Run);
            Anim.speed[eid] = Math.min(2, Math.max(0.5, Math.sqrt(sp2) / Config.anim.runRefSpeed.imp));
          } else {
            playClip(eid, ClipId.Idle);
            Anim.speed[eid] = 1;
          }
        } else if (kind === EnemyKind.Slime || kind === EnemyKind.Blight) {
          // The hop clip is the burst itself; squatting between hops is the idle wobble.
          if (mode === 1) {
            playClip(eid, ClipId.Run);
            Anim.speed[eid] = kind === EnemyKind.Blight ? 0.85 : 1;
          } else {
            playClip(eid, ClipId.Idle);
            Anim.speed[eid] = 1;
          }
        } else if (kind === EnemyKind.Mimic) {
          // Asleep: frozen chest. Awake: hop is the run, squat is the idle.
          if (mode < 2) {
            playClip(eid, ClipId.Idle);
            Anim.speed[eid] = mode === 1 ? 2.5 : 0;
          } else {
            playClip(eid, mode === 3 ? ClipId.Run : ClipId.Idle);
            Anim.speed[eid] = 1.2;
          }
        } else if (kind === EnemyKind.Skull || kind === EnemyKind.Bat) {
          // Flyers: lunge/dive uses the flight clip at full tilt, hovering flutters slower.
          const bursting = mode === 1;
          playClip(eid, bursting ? ClipId.Run : ClipId.Idle);
          Anim.speed[eid] = bursting ? 1.4 : 1;
        } else if (kind === EnemyKind.Cinder) {
          // Always closing; the flight clip speeds up with the chase ramp.
          playClip(eid, ClipId.Run);
          Anim.speed[eid] = Math.min(2.6, 0.9 + Math.sqrt(sp2) / Config.anim.runRefSpeed.imp);
        } else if (kind === EnemyKind.Wraith) {
          // Drift hovers; the lunge is the flight clip at full tilt. Fade/reform hold idle.
          playClip(eid, mode === 3 ? ClipId.Run : ClipId.Idle);
          Anim.speed[eid] = mode === 3 ? 1.6 : 0.8;
        } else if (sp2 > RUN_THRESHOLD_SQ) {
          playClip(eid, ClipId.Run);
          const ref = golem ? Config.anim.runRefSpeed.golem : brute ? Config.anim.runRefSpeed.brute : Config.anim.runRefSpeed.imp;
          Anim.speed[eid] = Math.min(2.2, Math.max(0.4, Math.sqrt(sp2) / ref));
        } else {
          playClip(eid, ClipId.Idle);
          Anim.speed[eid] = 1;
        }
        if (Math.abs(vx) > 0.25 && !(kind === EnemyKind.Mimic && mode < 2)) Sprite.face[eid] = vx < 0 ? -1 : 1;
        break;
      }
      case SpriteId.Dragon: {
        // Breath clips are driven by the DragonSystem via lock; otherwise flap.
        playClip(eid, ClipId.Idle);
        Anim.speed[eid] = 0.8 + Math.min(1.2, DragonTag.speed[eid] / 12);
        break;
      }
      case SpriteId.BossLich:
      case SpriteId.BossDruid:
      case SpriteId.BossMagus:
      case SpriteId.BossWarlord:
      case SpriteId.BossSkullKing:
      case SpriteId.BossColossus:
      case SpriteId.BossMatriarch:
      case SpriteId.BossSentinel:
      case SpriteId.BossHexweaver:
      case SpriteId.BossAshmaw:
      case SpriteId.BossBride: {
        // Cast clips are locked in by BossSystem at telegraph time. Between casts a
        // fresh, heavy hit plays the flinch; walkers and flyers run their move clip
        // while they are actually moving; otherwise idle/hover.
        if (Enemy.stagger[eid] > 0.2) {
          playClip(eid, ClipId.Hurt, Anim.done[eid] === 1, true);
          Anim.speed[eid] = 1;
        } else if (Velocity.x[eid] * Velocity.x[eid] + Velocity.y[eid] * Velocity.y[eid] > 1.2) {
          playClip(eid, ClipId.Run);
          Anim.speed[eid] = id === SpriteId.BossWarlord ? 0.9 : 1.1;
        } else {
          playClip(eid, ClipId.Idle);
          Anim.speed[eid] = 1;
        }
        break;
      }
      default:
        break;
    }
  }

  private advance(eid: number, dt: number): void {
    const id = Sprite.id[eid] as SpriteId;
    const clipId = Anim.clip[eid] as ClipId;
    const clip = Anims.clip(id, clipId);
    if (clip.len <= 1 && clip.loop) {
      Anim.frame[eid] = 0;
      return;
    }
    const prevFrame = Anim.frame[eid];
    Anim.time[eid] += dt * Anim.speed[eid];
    const raw = Math.floor(Anim.time[eid] * clip.fps);
    let frame: number;
    if (clip.loop) {
      frame = raw % clip.len;
    } else if (raw >= clip.len) {
      frame = clip.len - 1;
      Anim.done[eid] = 1;
    } else {
      frame = raw;
    }
    Anim.frame[eid] = frame;

    // Footsteps: dust on the stride frames of a run cycle.
    if (clipId === ClipId.Run && frame !== prevFrame && clip.len >= 4 && (frame === 0 || frame === clip.len >> 1)) {
      const heavy = id === SpriteId.Golem || id === SpriteId.Brute;
      if (id === SpriteId.Player || heavy) {
        const ctx = this.ctx;
        emitParticles(ctx.fx, ParticleKind.Dust, Position.x[eid], Position.y[eid] - Radius.r[eid] * 0.8, -Velocity.x[eid], -Velocity.y[eid], heavy ? 1 : 0.55);
        if (id === SpriteId.Player) {
          Sprite.squash[eid] = Math.max(Sprite.squash[eid], Config.anim.player.footstepSquash);
          if (Player.swiftTime[eid] > 0) {
            emitParticles(ctx.fx, ParticleKind.Embers, Position.x[eid], Position.y[eid] - Radius.r[eid] * 0.6, -Velocity.x[eid], -Velocity.y[eid], 0.45, Config.pickups.swift.tint);
          }
        } else if (heavy) {
          ctx.fx.shakeX += (ctx.rng.next() - 0.5) * 0.12;
          ctx.fx.shakeY += 0.08;
          Sprite.squash[eid] = Math.max(Sprite.squash[eid], 0.25);
        }
      }
    }
  }
}

/**
 * Turns a flip-only sprite toward (dx, dy). Zero vectors keep the current facing; a
 * direction only flips the body once its x component clears the hysteresis cone around
 * vertical, so straight up/down and near-vertical diagonals don't chatter.
 */
function faceToward(eid: number, dx: number, dy: number): void {
  const len = Math.hypot(dx, dy);
  if (len < 1e-4) return;
  const nx = dx / len;
  const face = Sprite.face[eid] || 1;
  if (nx * face < -FACE_FLIP_SIN) Sprite.face[eid] = -face;
  else if (Sprite.face[eid] === 0) Sprite.face[eid] = nx < 0 ? -1 : 1;
}
